import { ConversationStatus, PanelCampaignStatus } from '@/types/pulse';
import {
  PanelCampaign,
  PanelCampaignConversation,
  PanelCampaignConversationWConversation
} from '@/types/pulsedb';
import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';

export const getPanelCampaigns = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number,
    filters?: {
      status?: PanelCampaignStatus | null;
    }
  ): Promise<PanelCampaign[]> => {
    console.log(
      'Querying campaigns for interviewId:',
      interviewId,
      'with filters:',
      filters
    );

    let query = supabase
      .from('panel_campaigns')
      .select('*')
      .eq('interview_id', interviewId)
      .eq('deleted', false);

    if (filters?.status !== null && filters?.status !== undefined) {
      query = query.eq('status', filters.status);
    }

    const { data } = await query
      .order('id', { ascending: false })
      .throwOnError();
    return data || [];
  }
);

export const getPanelCampaignBySlug = cache(
  async (
    supabase: SupabaseClient,
    slug: string
  ): Promise<PanelCampaign | null> => {
    const { data } = await supabase
      .from('panel_campaigns')
      .select('*')
      .eq('slug', slug)
      .maybeSingle()
      .throwOnError();
    return data || null;
  }
);

/** Returns the panel campaign by id. Throws if not found. */
export const getPanelCampaign = cache(
  async (
    supabase: SupabaseClient,
    campaignId: number
  ): Promise<PanelCampaign> => {
    const { data } = await supabase
      .from('panel_campaigns')
      .select('*')
      .eq('id', campaignId)
      .single()
      .throwOnError();
    return data;
  }
);

export const getPanelCampaignConversations = cache(
  async (
    supabase: SupabaseClient,
    campaignId: number
  ): Promise<PanelCampaignConversationWConversation[]> => {
    console.log('Querying conversations for campaignId:', campaignId);
    const { data, error } = await supabase
      .from('panel_campaign_conversations')
      .select('*, conversations!inner(*)')
      .eq('panel_campaign_id', campaignId)
      .neq('conversations.status', ConversationStatus.FLAGGED)
      .eq('conversations.deleted', false)
      .eq('conversations.hidden', false);

    if (error) {
      console.error('Supabase error:', error);
      return [];
    }

    console.log('Raw conversation data from supabase:', data);
    return data || [];
  }
);

export const createPanelCampaign = async (
  supabase: SupabaseClient,
  data: Omit<PanelCampaign, 'id' | 'created_at' | 'slug'>,
  slug: string
) => {
  const { data: newCampaign } = await supabase
    .from('panel_campaigns')
    .insert({ ...data, slug })
    .select('*')
    .throwOnError();
  return newCampaign?.[0] as unknown as PanelCampaign | null;
};

export const updatePanelCampaign = async (
  supabase: SupabaseClient,
  id: number,
  data: Partial<Omit<PanelCampaign, 'id' | 'created_at'>>
) => {
  const { data: updatedCampaign } = await supabase
    .from('panel_campaigns')
    .update(data)
    .eq('id', id)
    .select('*')
    .throwOnError();
  return updatedCampaign?.[0] as unknown as PanelCampaign | null;
};

export const deletePanelCampaign = async (
  supabase: SupabaseClient,
  id: number
) => {
  await supabase
    .from('panel_campaigns')
    .update({ deleted: true })
    .eq('id', id)
    .throwOnError();
};

export const pausePanelCampaign = async (
  supabase: SupabaseClient,
  id: number
) => {
  await supabase
    .from('panel_campaigns')
    .update({ status: PanelCampaignStatus.PAUSED })
    .eq('id', id)
    .throwOnError();
};

export const resumePanelCampaign = async (
  supabase: SupabaseClient,
  id: number
) => {
  await supabase
    .from('panel_campaigns')
    .update({ status: PanelCampaignStatus.ACTIVE })
    .eq('id', id)
    .throwOnError();
};

export const createPanelCampaignConversation = async (
  supabase: SupabaseClient,
  data: Omit<PanelCampaignConversation, 'id' | 'created_at'>,
  options?: { ignoreConflict?: boolean }
) => {
  const { data: newConversation, error } = await supabase
    .from('panel_campaign_conversations')
    .insert(data)
    .select('*');
  if (error) {
    // 23505 = unique_violation → the row already exists; treat as success when
    // the caller opts in (idempotent re-runs, e.g. postprocess replays).
    if (options?.ignoreConflict && error.code === '23505') return null;
    throw error;
  }
  return newConversation?.[0] as unknown as PanelCampaignConversation | null;
};

export const getCountCompletedPanelCampaignConversations = cache(
  async (
    supabase: SupabaseClient,
    campaignId: number
  ): Promise<number | null> => {
    console.log('Querying for campaign ID:', campaignId);

    const { count } = await supabase
      .from('panel_campaign_conversations')
      .select('conversations!inner(*)', { count: 'exact', head: true })
      .eq('conversations.status', ConversationStatus.COMPLETED)
      .eq('panel_campaign_id', campaignId)
      .eq('conversations.deleted', false)
      .eq('conversations.hidden', false)
      .throwOnError();
    console.log('Query result count:', count);
    return count;
  }
);
